import { CalendarDetailsVisibility, ID, Task } from '@griply/types';
import { getTaskCalendarSyncCandidates, LinkedItemGoal } from '@griply/utils';
import { useGoals } from 'features/goals';
import { useCallback, useMemo } from 'react';
import {
  CalendarEventSyncState,
  CalendarTaskSync,
} from 'shared/types/calendar-task-sync';

import { useExternalCalendarConnections } from './use-external-calendar-connections';
import { useSetTaskCalendarOverride } from './use-set-task-calendar-override';

export const useCalendarTaskSync = (tasks: Task[]): CalendarTaskSync => {
  const connections = useExternalCalendarConnections();
  const goals = useGoals();
  const setTaskOverride = useSetTaskCalendarOverride();

  const states = useMemo(() => {
    const goalsById = new Map<ID, LinkedItemGoal>(
      goals.map((goal) => [goal.id, goal]),
    );

    return tasks.reduce<Map<ID, CalendarEventSyncState>>((map, task) => {
      const candidates = getTaskCalendarSyncCandidates({
        task,
        connections,
        goalsById,
      });
      if (!candidates.length) {
        return map;
      }

      const syncedCandidates = candidates.filter(({ synced }) => synced);
      map.set(task.id, {
        isSynced: !!syncedCandidates.length,
        isHidden:
          !!syncedCandidates.length &&
          syncedCandidates.every(
            ({ visibility }) => visibility === CalendarDetailsVisibility.Hidden,
          ),
      });
      return map;
    }, new Map());
  }, [tasks, connections, goals]);

  const onToggleVisibility = useCallback(
    (task: Task, isHidden: boolean) =>
      setTaskOverride(task, {
        detailsVisibility: isHidden
          ? CalendarDetailsVisibility.Visible
          : CalendarDetailsVisibility.Hidden,
      }),
    [setTaskOverride],
  );

  const onToggleSync = useCallback(
    (task: Task, isSynced: boolean) =>
      setTaskOverride(task, { synced: !isSynced }),
    [setTaskOverride],
  );

  return useMemo(
    () => ({ states, onToggleVisibility, onToggleSync }),
    [states, onToggleVisibility, onToggleSync],
  );
};
